/**
 * Buy More Save Progress Bar Component
 * Supports: "Buy X Get Y Free" OR "Buy X Get Z% Off"
 */
class BuyMoreSaveProgress extends HTMLElement {
  constructor() {
    super();
    this.itemsRequired = parseInt(this.dataset.itemsRequired) || 4;
    this.offerType = this.dataset.offerType || 'percentage';
    this.discountPercentage = parseInt(this.dataset.discountPercentage) || 20;
    this.currentCount = parseInt(this.dataset.cartCount) || 0;
    
    this.itemsRemainingEl = this.querySelector('[data-items-remaining]');
    this.successMessage = this.querySelector('.bms__message-success');
    this.progressMessage = this.querySelector('.bms__message-progress');
    this.steps = this.querySelectorAll('[data-step]');
    this.trackFill = this.querySelector('.bms__track-fill');
    
    this.cartUpdateUnsubscriber = null;
  }

  connectedCallback() {
    // Subscribe to cart updates
    if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
      this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        this.onCartUpdate.bind(this)
      );
    }
    
    // Also listen for custom cart refresh events
    document.addEventListener('cart:refresh', this.onCartRefresh.bind(this));
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    document.removeEventListener('cart:refresh', this.onCartRefresh.bind(this));
  }

  onCartUpdate(event) {
    if (event && event.cart) {
      this.updateProgress(event.cart.item_count);
    }
  }

  onCartRefresh(event) {
    // Fetch current cart count
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        this.updateProgress(cart.item_count);
      })
      .catch(console.error);
  }

  updateProgress(newCount) {
    const previousCount = this.currentCount;
    this.currentCount = newCount;
    
    const itemsInCurrentSet = newCount % this.itemsRequired;
    const completeSets = Math.floor(newCount / this.itemsRequired);
    const itemsUntilNext = itemsInCurrentSet === 0 && newCount > 0 
      ? 0 
      : this.itemsRequired - itemsInCurrentSet;
    const offerAchieved = itemsInCurrentSet === 0 && newCount > 0;
    
    // Update items remaining text
    if (this.itemsRemainingEl) {
      this.itemsRemainingEl.textContent = itemsUntilNext;
    }
    
    // Toggle messages
    if (this.successMessage && this.progressMessage) {
      this.successMessage.classList.toggle('hidden', !offerAchieved);
      this.progressMessage.classList.toggle('hidden', offerAchieved);
    }
    
    // Update achieved state on container
    this.classList.toggle('bms--achieved', offerAchieved);
    
    // Hide when cart is empty
    this.classList.toggle('bms--empty', newCount === 0);
    
    // Update progress bar track
    this.updateTrack(itemsInCurrentSet, offerAchieved);
    
    // Update steps
    this.updateSteps(itemsInCurrentSet, offerAchieved, newCount);
    
    // Celebration animation when achieving offer
    const previousSets = Math.floor(previousCount / this.itemsRequired);
    if (completeSets > previousSets && completeSets > 0) {
      this.celebrate();
    }
  }

  updateTrack(itemsInCurrentSet, offerAchieved) {
    if (this.trackFill) {
      const percentage = offerAchieved ? 100 : (itemsInCurrentSet / this.itemsRequired) * 100;
      this.trackFill.style.width = `${percentage}%`;
    }
  }

  updateSteps(itemsInCurrentSet, offerAchieved, cartCount) {
    this.steps.forEach(step => {
      const stepIndex = parseInt(step.dataset.step);
      let isActive = false;
      let isCurrent = false;
      
      if (cartCount === 0) {
        isActive = false;
        isCurrent = false;
      } else if (offerAchieved) {
        isActive = true;
        isCurrent = false;
      } else {
        isActive = stepIndex <= itemsInCurrentSet;
        isCurrent = stepIndex === itemsInCurrentSet;
      }
      
      step.classList.toggle('bms__step--active', isActive);
      step.classList.toggle('bms__step--current', isCurrent);
    });
  }

  celebrate() {
    // Add celebration animation to the reward step
    const rewardStep = this.querySelector('.bms__step--reward');
    if (rewardStep) {
      rewardStep.classList.add('bms__step--celebrating');
      setTimeout(() => {
        rewardStep.classList.remove('bms__step--celebrating');
      }, 600);
    }
  }
}

customElements.define('buy-more-save-progress', BuyMoreSaveProgress);
